import React, { useState, useEffect, useContext, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles(theme => ({
    logList: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        minHeight: 200,
    },
    logEntry: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        padding: theme.spacing(0.5, 1),
        fontSize: 12,
        "& summary": {
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
            flexWrap: "wrap",
            listStyle: "none",
            outline: "none",
        },
    },
    logUrl: {
        fontFamily: "monospace",
        fontSize: 11,
        color: theme.palette.text.secondary,
        wordBreak: "break-all",
        flex: 1,
        minWidth: 120,
    },
    timestamp: {
        fontFamily: "monospace",
        fontSize: 11,
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
    },
    jsonBlock: {
        background: theme.palette.type === "dark" ? "#1e1e1e" : "#f5f6fa",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        padding: theme.spacing(1),
        margin: theme.spacing(0.5, 0),
        fontSize: 11,
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxHeight: 260,
        overflowY: "auto",
    },
    jsonLabel: {
        fontWeight: 600,
        fontSize: 11,
        marginTop: theme.spacing(0.5),
    },
    emptyState: {
        textAlign: "center",
        color: theme.palette.text.secondary,
        padding: theme.spacing(4),
    },
    chipSent: {
        background: "#1976d2",
        color: "#fff",
        height: 20,
        fontSize: 10,
    },
    chipReceived: {
        background: "#2e7d32",
        color: "#fff",
        height: 20,
        fontSize: 10,
    },
    chipError: {
        background: "#c62828",
        color: "#fff",
        height: 20,
        fontSize: 10,
    },
    chipStatus: {
        height: 20,
        fontSize: 10,
    },
}));

const isErrorStatus = (log) => {
    if (log.error) return true;
    const st = Number(log.status);
    return Number.isFinite(st) && (st < 200 || st >= 300);
};

const formatTime = (ts) => {
    try {
        return new Date(ts).toLocaleString(i18n.language);
    } catch {
        return String(ts || "");
    }
};

const AIHubLogsModal = ({ open, onClose, promptId }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const companyId = user?.companyId;
    const socketManager = useContext(SocketContext);

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchLogs = useCallback(async () => {
        if (!promptId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/prompt/${promptId}/test-logs`);
            setLogs(data.logs || []);
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    }, [promptId]);

    useEffect(() => {
        if (open) fetchLogs();
    }, [open, fetchLogs]);

    // Atualização em tempo real via websocket enquanto o popup estiver aberto
    useEffect(() => {
        if (!open || !companyId || !promptId) return;

        const socket = socketManager.getSocket(companyId);
        const eventName = `company-${companyId}-aiHubLog`;
        const handler = (entry) => {
            // Exibe apenas os logs do agente que está sendo editado
            if (!entry || Number(entry.promptId) !== Number(promptId)) return;
            setLogs(prev => [entry, ...prev].slice(0, 200));
        };

        socket.on(eventName, handler);
        return () => {
            socket.off(eventName, handler);
        };
    }, [open, companyId, promptId, socketManager]);

    const handleClearLogs = async () => {
        try {
            await api.delete(`/prompt/${promptId}/test-logs`);
            setLogs([]);
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle>
                {i18n.t("promptModal.logs.title")}
                <Typography variant="caption" display="block" color="textSecondary">
                    {i18n.t("promptModal.logs.subtitle")}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box className={classes.logList}>
                    {loading && (
                        <Box textAlign="center" p={2}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    {!loading && logs.length === 0 && (
                        <Box className={classes.emptyState}>
                            <Typography variant="body2">{i18n.t("promptModal.logs.empty")}</Typography>
                            <Typography variant="caption">
                                {i18n.t("promptModal.logs.emptyHint")}
                            </Typography>
                        </Box>
                    )}
                    {logs.map(log => (
                        <details key={log.id} className={classes.logEntry}>
                            <summary>
                                <span className={classes.timestamp}>{formatTime(log.timestamp)}</span>
                                <Chip
                                    size="small"
                                    label={log.direction === "sent" ? `→ ${i18n.t("promptModal.logs.sent")}` : `← ${i18n.t("promptModal.logs.received")}`}
                                    className={isErrorStatus(log)
                                        ? classes.chipError
                                        : log.direction === "sent" ? classes.chipSent : classes.chipReceived}
                                />
                                <Chip size="small" variant="outlined" label={log.kind} className={classes.chipStatus} />
                                {log.status !== undefined && log.status !== null && (
                                    <Chip size="small" variant="outlined" label={`status: ${log.status}`} className={classes.chipStatus} />
                                )}
                                <span className={classes.logUrl}>{log.url}</span>
                            </summary>
                            {log.error && (
                                <>
                                    <Typography className={classes.jsonLabel} color="error">{i18n.t("promptModal.logs.error")}</Typography>
                                    <pre className={classes.jsonBlock}>{log.error}</pre>
                                </>
                            )}
                            {log.payload !== undefined && log.payload !== null && (
                                <>
                                    <Typography className={classes.jsonLabel}>Payload</Typography>
                                    <pre className={classes.jsonBlock}>
                                        {typeof log.payload === "string" ? log.payload : JSON.stringify(log.payload, null, 2)}
                                    </pre>
                                </>
                            )}
                            {log.response !== undefined && log.response !== null && (
                                <>
                                    <Typography className={classes.jsonLabel}>{i18n.t("promptModal.logs.response")}</Typography>
                                    <pre className={classes.jsonBlock}>
                                        {typeof log.response === "string" ? log.response : JSON.stringify(log.response, null, 2)}
                                    </pre>
                                </>
                            )}
                        </details>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClearLogs} color="secondary" variant="outlined">
                    {i18n.t("promptModal.logs.clear")}
                </Button>
                <Button onClick={fetchLogs} color="default" variant="outlined">
                    {i18n.t("promptModal.logs.reload")}
                </Button>
                <Button onClick={onClose} color="primary" variant="contained">
                    {i18n.t("common.close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AIHubLogsModal;
